
import React, { useState, useEffect, useRef } from "react";

interface VideoBackgroundProps {
  videoUrl: string;
  className?: string;
  overlay?: boolean;
  overlayOpacity?: number;
}

export const VideoBackground: React.FC<VideoBackgroundProps> = ({
  videoUrl,
  className = "absolute inset-0 w-full h-full object-cover z-0",
  overlay = true,
  overlayOpacity = 0.2
}) => {
  const [blobUrl, setBlobUrl] = useState<string>('');
  const blobUrlRef = useRef<string | null>(null);
  const processedVideoUrlRef = useRef<string>('');

  useEffect(() => {
    // Skip if we've already processed this exact videoUrl
    if (processedVideoUrlRef.current === videoUrl && blobUrlRef.current) {
      console.log('VideoBackground: Skipping conversion, already processed this video URL');
      return;
    }

    // If it's a data URL, convert it to a blob URL for better browser compatibility
    if (videoUrl.startsWith('data:video/')) {
      console.log('VideoBackground: Converting data URL to blob URL');
      // Convert data URL to blob
      fetch(videoUrl)
        .then(res => res.blob())
        .then(blob => {
          const newBlobUrl = URL.createObjectURL(blob);
          console.log('VideoBackground: Created blob URL from data URL:', newBlobUrl);
          
          // Clean up the OLD blob URL AFTER creating the new one
          const oldBlobUrl = blobUrlRef.current;
          if (oldBlobUrl && oldBlobUrl.startsWith('blob:')) {
            console.log('VideoBackground: Cleaning up previous blob URL:', oldBlobUrl);
            URL.revokeObjectURL(oldBlobUrl);
          }
          
          blobUrlRef.current = newBlobUrl;
          processedVideoUrlRef.current = videoUrl;
          setBlobUrl(newBlobUrl);
        })
        .catch(error => {
          console.error('VideoBackground: Failed to convert data URL to blob:', error);
          // Fallback to original URL
          processedVideoUrlRef.current = videoUrl;
          setBlobUrl(videoUrl);
        });
    } else {
      // For non-data URLs (like Pexels videos), use directly
      // Clean up any previous blob URL
      if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
        console.log('VideoBackground: Cleaning up blob URL (switching to regular URL):', blobUrlRef.current);
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      processedVideoUrlRef.current = videoUrl;
      setBlobUrl(videoUrl);
    }

    // Cleanup function - only runs when component unmounts or videoUrl changes
    return () => {
      // Only clean up if we're actually unmounting or the videoUrl is changing
      if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:') && processedVideoUrlRef.current !== videoUrl) {
        console.log('VideoBackground: Cleaning up blob URL on videoUrl change:', blobUrlRef.current);
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [videoUrl]);

  // Don't render until we have a URL to use
  if (!blobUrl) {
    return null;
  }

  return (
    <>
      <video
        key={blobUrl}
        src={blobUrl}
        autoPlay
        muted
        loop
        playsInline
        className={className}
        onError={(e) => console.error('VideoBackground: Video failed to load:', e)}
        onLoadStart={() => console.log('VideoBackground: Video loading started')}
        onLoadedData={() => console.log('VideoBackground: Video loaded successfully')}
        onPlay={() => console.log('VideoBackground: Video started playing')}
        style={{ backgroundColor: 'transparent' }}
      />
      {overlay && (
        <div
          className="absolute inset-0 bg-black z-0"
          style={{ opacity: overlayOpacity }}
        />
      )}
    </>
  );
};
